import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Handshake, PenSquare, BookOpen, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const BE = process.env.REACT_APP_BACKEND_URL;

async function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
}

const STATE_LABELS = {
  draft: "Brouillon",
  submitted: "Soumise",
  under_review: "En revue",
  approved: "Approuvée",
  published: "Publiée",
  expired: "Expirée",
  rejected: "Refusée",
};

export default function PartnerPortalPage() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // For admins, list all orgs; partners will get their linked orgs
        // implicitly filtered by /offers (empty result if none).
        let all = [];
        try {
          all = await api("/api/partner-platform/orgs");
        } catch {}
        setOrgs(all);
        if (all.length) setSelectedOrgId(all[0].id);
        const off = await api("/api/partner-platform/offers");
        setOffers(off);
      } catch (e) {
        toast.error("Impossible de charger le portail partenaire.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedOrgId) return;
    (async () => {
      try {
        const [k, agg] = await Promise.all([
          api(`/api/partner-platform/knowledge?org_id=${selectedOrgId}`),
          api(`/api/partner-platform/analytics/aggregate?org_id=${selectedOrgId}`),
        ]);
        setKnowledge(k);
        setAggregate(agg);
      } catch {
        setKnowledge([]);
        setAggregate(null);
      }
    })();
  }, [selectedOrgId]);

  const transition = async (offerId, target) => {
    try {
      await api(`/api/partner-platform/offers/${offerId}/transition`, {
        method: "POST",
        body: JSON.stringify({ target_state: target }),
      });
      toast.success(`Offre → ${STATE_LABELS[target] || target}`);
      const off = await api("/api/partner-platform/offers");
      setOffers(off);
    } catch (e) {
      toast.error(e.message || "Transition impossible.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <p className="text-slate-500 animate-pulse">Chargement du portail…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4 text-center">
        <p className="text-slate-600">Connectez-vous pour accéder au portail partenaire.</p>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-secondary py-10 md:py-16" data-testid="partner-portal-page">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-8">
          <Handshake className="h-7 w-7 text-accent" />
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-accent">Portail</p>
            <h1 className="font-heading text-3xl md:text-4xl text-primary">Espace partenaire</h1>
          </div>
        </div>

        {orgs.length > 1 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {orgs.map((o) => (
              <button
                key={o.id}
                onClick={() => setSelectedOrgId(o.id)}
                className={`px-3 py-1.5 rounded-full text-sm border transition ${
                  selectedOrgId === o.id
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-slate-600 border-slate-200 hover:border-accent"
                }`}
                data-testid={`org-tab-${o.slug}`}
              >
                {o.name}
              </button>
            ))}
          </div>
        )}

        <Tabs defaultValue="offers">
          <TabsList>
            <TabsTrigger value="offers" data-testid="portal-tab-offers"><PenSquare className="h-4 w-4 mr-2" />Offres</TabsTrigger>
            <TabsTrigger value="knowledge" data-testid="portal-tab-knowledge"><BookOpen className="h-4 w-4 mr-2" />Contributions</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="portal-tab-analytics"><BarChart3 className="h-4 w-4 mr-2" />Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="offers" className="mt-6">
            {offers.length === 0 ? (
              <p className="text-slate-500 text-sm">Aucune offre pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {offers.map((o) => (
                  <Card key={o.id} className="border-primary/10">
                    <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-primary">{o.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {o.category || "sans catégorie"} · {o.audience}
                        </p>
                      </div>
                      <Badge variant="outline" data-testid={`offer-state-${o.id}`}>
                        {STATE_LABELS[o.workflow_state] || o.workflow_state}
                      </Badge>
                      <div className="flex gap-2 flex-wrap">
                        {o.workflow_state === "draft" && (
                          <Button size="sm" variant="outline" onClick={() => transition(o.id, "submitted")} data-testid={`offer-submit-${o.id}`}>
                            Soumettre
                          </Button>
                        )}
                        {(user.role === "admin" || user.role === "editor" || user.role === "super_admin") && (
                          <>
                            {o.workflow_state === "submitted" && (
                              <Button size="sm" variant="outline" onClick={() => transition(o.id, "under_review")}>En revue</Button>
                            )}
                            {o.workflow_state === "under_review" && (
                              <>
                                <Button size="sm" onClick={() => transition(o.id, "approved")}>Approuver</Button>
                                <Button size="sm" variant="ghost" onClick={() => transition(o.id, "rejected")}>Refuser</Button>
                              </>
                            )}
                            {o.workflow_state === "approved" && (
                              <Button size="sm" onClick={() => transition(o.id, "published")}>Publier</Button>
                            )}
                            {o.workflow_state === "published" && (
                              <Button size="sm" variant="ghost" onClick={() => transition(o.id, "expired")}>Archiver</Button>
                            )}
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="knowledge" className="mt-6">
            {knowledge.length === 0 ? (
              <p className="text-slate-500 text-sm">Aucune contribution.</p>
            ) : (
              <ul className="space-y-2">
                {knowledge.map((k) => (
                  <li key={k.id} className="bg-white rounded-lg p-3 border border-slate-100 text-sm">
                    <p className="font-semibold text-primary">{k.title}</p>
                    <p className="text-xs text-slate-500">{k.kind} · {k.destination || "—"}</p>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            {!aggregate ? (
              <p className="text-slate-500 text-sm">Aucune donnée analytique pour cet organisme.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(aggregate.totals || {}).map(([k, v]) => (
                  <Card key={k} className="border-primary/10">
                    <CardContent className="p-4">
                      <p className="text-xs uppercase text-slate-500">{k}</p>
                      <p className="font-heading text-2xl text-primary mt-1">{v}</p>
                    </CardContent>
                  </Card>
                ))}
                {Object.keys(aggregate.totals || {}).length === 0 && (
                  <p className="text-slate-500 text-sm col-span-full">Aucun événement enregistré.</p>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
